/**
 * Raport zarządu — teksty sekcji operations (`mbr.operations.*`). WŁAŚCICIEL: agent sekcji operations (docs/RAPORT-ZARZADU.md).
 * Podpięte już w i18n/mbr.ts — dopisuj klucze tutaj, nie w extra.ts. `en` typowane względem `pl`.
 * Własne kody uzgodnień sekcji: `checks: { KOD: '…' }` (panel szuka `mbr.checks.KOD`, potem `mbr.operations.checks.KOD`).
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrOperationsPl = {
  title: 'Magazyn i jakość',
  description: 'Kompletacja, inwentaryzacja, reklamacje w 8 kategoriach, utylizacja i produkcja.',
  tabs: {
    warehouse: 'Magazyn',
    complaints: 'Reklamacje',
    disposal: 'Utylizacja',
    production: 'Produkcja',
  },
  segments: {
    warehouse: '{value} min na zamówienie',
    complaints: 'reklamacje: {value}',
    disposal: 'utylizacja: {value}',
    production: 'różnica: {value}',
    productionMissing: 'brak danych za okres',
    ai: 'Wnioski AI: {count}',
  },
  ai: {
    notesTitle: 'Wnioski AI dla tej części',
  },
  kpi: {
    complaints: 'Reklamacje',
    complaintsPctOfOrders: 'Reklamacje / zamówienia',
    complaintsPerDeliveryDay: 'Reklamacje na dzień dostaw',
    complaintsValue: 'Wartość reklamacji',
    disposalValue: 'Utylizacja',
    disposalNote: '{pct}% sprzedaży',
    disposalPctOfSales: 'Utylizacja / sprzedaż',
    pickingMinutesPerOrder: 'Kompletacja — min na zamówienie',
    orders: 'Zamówienia',
    ordersPerDeliveryDay: 'Zamówienia na dzień dostaw',
    pickingHours: 'Godziny kompletacji',
    pickingHoursPerDeliveryDay: 'Godziny kompletacji na dzień dostaw',
    orderLines: 'Pozycje w zamówieniach',
    pickingMinutesPerLine: 'Kompletacja — min na pozycję',
    linesPerOrder: 'Pozycje na zamówienie',
    inventoryBalance: 'Inwentaryzacja — saldo',
    productionSales: 'Produkcja — sprzedaż wyrobów',
    productionCost: 'Produkcja — koszt wyrobów',
  },
  checks: {
    COMPLAINT_CATEGORIES_SUM:
      'Suma 8 kategorii reklamacji ({actual}) różni się od liczby łącznie ({expected}).',
    PRODUCTION_MISSING_NOT_ZERO: 'Produkcja: brak danych albo same zera — w raporcie „brak danych”, nie 0.',
    WAREHOUSE_ORDERS_VS_ERP:
      'Zamówienia z formularza magazynu ({actual}) różnią się od ERP ({expected}) o {diffPct}%.',
  },
  categories: {
    QUALITY: 'Jakość',
    MISSING_IN_DELIVERY: 'Brak w dostawie',
    MISSING_ON_DOCUMENT: 'Brak na dokumencie',
    WEIGHT_QUANTITY: 'Waga / ilość',
    WRONG_PRODUCT: 'Błąd towarowy',
    EXPIRY_DATE: 'Termin przydatności',
    PRICE: 'Cena',
    OTHER: 'Inne',
  },
  statuses: {
    OPEN: 'Otwarta',
    IN_PROGRESS: 'W toku',
    ACCEPTED: 'Uznana',
    REJECTED: 'Odrzucona',
    CLOSED: 'Zamknięta',
  },
  dimensions: {
    category: 'Kategoria',
    product: 'Produkt',
    route: 'Trasa',
    driver: 'Kierowca / auto',
    customer: 'Klient',
  },
  sources: {
    ERP: 'z ERP',
    WAREHOUSE: 'z formularza magazynu',
    REGISTER: 'z rejestru',
    IMPORT: 'z importu',
    MISSING: 'brak danych',
  },
  common: {
    unassigned: 'bez przypisania',
    scopedNote:
      'Zamówienia i reklamacje Twojego zespołu. Wskaźniki łączące dane całej firmy z zespołem są ukryte.',
    all: 'Wszystkie',
    share: 'Udział',
    count: 'Liczba',
    value: 'Wartość',
    total: 'Razem',
    noData: 'brak danych',
    scopedChip: 'Twój zespół',
  },
  warehouse: {
    title: 'Kompletacja i zamówienia',
    sources: 'Zamówienia {orders}, pozycje {lines}',
    inventoryTitle: 'Inwentaryzacja',
    inventoryPlus: 'Nadwyżki (+)',
    inventoryMinus: 'Niedobory (−)',
    inventoryBalance: 'Saldo',
    inventoryHint: 'Nadwyżki minus niedobory w okresie bieżącym',
    inventorySplit: 'Udział nadwyżek i niedoborów',
    trendTitle: 'Zamówienia i minuty kompletacji — 12 miesięcy',
  },
  complaints: {
    categoriesTitle: 'Reklamacje w 8 kategoriach',
    trendTitle: 'Trend 12 miesięcy wg kategorii',
    breakdownTitle: 'Gdzie powstają reklamacje (okres bieżący)',
    statusesTitle: 'Statusy w okresie',
    openRegister: 'Rejestr reklamacji',
    sourceNote: 'Liczby {source}',
    current: 'Okres bieżący',
    base: 'Okres bazowy',
    flagged: 'Wskazane przez AI',
    breakdownHint: 'Im ciemniejsze pole, tym więcej reklamacji w danej kategorii.',
    heatLegend: 'Mniej',
    heatMore: 'więcej reklamacji',
    noRegister: 'Macierz powstaje z rejestru reklamacji — w tym okresie liczby pochodzą z importu.',
  },
  disposal: {
    topTitle: 'Najwięcej utylizacji — produkty',
    entries: 'Wpisy',
    openRegister: 'Rejestr utylizacji',
    trendTitle: 'Utylizacja — 12 miesięcy',
    trendHint: 'Wartość towaru zutylizowanego w miesiącu',
    trendMonth: 'Miesiąc raportu',
    trendOther: 'Poprzednie miesiące',
    monthTotal: 'Razem w miesiącu',
  },
  production: {
    title: 'Produkcja — wyroby gotowe',
    sales: 'Sprzedaż wyrobów',
    cost: 'Koszt wyrobów',
    diff: 'Różnica',
    diffPct: 'Różnica / sprzedaż',
    missingNote: 'Brak danych o produkcji za ten okres — pole puste to „brak danych”, nie 0.',
    missingTitle: 'Brak danych o produkcji za {period}',
    missingHint:
      'W raporcie to pole pokazuje „brak danych”, nie 0. Sprzedaż i koszt wyrobów wpisuje się w formularzu „Produkcja” zamknięcia miesiąca.',
    fillForm: 'Uzupełnij formularz Produkcja',
    diffHint: 'Sprzedaż minus koszt wyrobów gotowych',
    diffPctHint: 'Różnica jako % sprzedaży wyrobów',
    trendTitle: 'Sprzedaż i koszt wyrobów — 12 miesięcy',
  },
  register: {
    complaintsTitle: 'Rejestr reklamacji',
    complaintsDescription: 'Każda reklamacja z kategorią, klientem, trasą, kierowcą, produktem i dokumentem.',
    disposalsTitle: 'Rejestr utylizacji',
    disposalsDescription: 'Wpisy utylizacji (RW) z produktem, wartością i przyczyną.',
    back: 'Wróć do sekcji',
    add: 'Dodaj',
    edit: 'Edytuj',
    remove: 'Usuń',
    removeConfirm: 'Usunąć ten wpis? Tej operacji nie można cofnąć.',
    save: 'Zapisz',
    cancel: 'Anuluj',
    saved: 'Zapisano',
    removed: 'Usunięto',
    search: 'Szukaj (opis, dokument, produkt, klient)',
    empty: 'Brak wpisów dla wybranych filtrów.',
    readOnly: 'Miesiąc zamknięty — tylko podgląd',
    showing: '{from}–{to} z {total}',
    prev: 'Poprzednie',
    next: 'Następne',
    totalValue: 'Wartość razem: {value}',
    summary: 'Wpisy: {count} · razem {value}',
    analysisTitle: 'Analiza: {dimension} × kategoria',
    analysisEmpty: 'Brak reklamacji do analizy.',
    errors: {
      MBR_MONTH_CLOSED: 'Miesiąc jest zamknięty — otwórz go ponownie, aby zmienić dane.',
      MBR_COMPLAINT_SCOPE: 'Możesz rejestrować reklamacje tylko klientów ze swojego zakresu.',
      MBR_COMPLAINT_DUPLICATE_REF: 'Ten numer protokołu jest już w rejestrze.',
      MBR_DISPOSAL_DUPLICATE_REF: 'Ten numer dokumentu RW jest już w rejestrze.',
      MBR_REFERENCE_INVALID: 'Wybrany rekord nie istnieje — wybierz z listy podpowiedzi.',
      generic: 'Nie udało się zapisać.',
    },
  },
  fields: {
    date: 'Data',
    category: 'Kategoria',
    status: 'Status',
    customer: 'Klient',
    route: 'Trasa',
    driver: 'Kierowca',
    vehicle: 'Pojazd',
    product: 'Produkt',
    productName: 'Produkt (spoza katalogu)',
    quantity: 'Ilość',
    value: 'Wartość, zł',
    description: 'Opis',
    externalRef: 'Nr protokołu / dokumentu',
    reason: 'Przyczyna',
    documentRef: 'Nr dokumentu RW',
    lookupPlaceholder: 'Zacznij pisać, aby wyszukać…',
    clear: 'Wyczyść',
  },
};

export const mbrOperationsEn: Shape<typeof mbrOperationsPl> = {
  title: 'Warehouse & quality',
  description: 'Picking, stocktaking, complaints in 8 categories, disposal and production.',
  tabs: {
    warehouse: 'Warehouse',
    complaints: 'Complaints',
    disposal: 'Disposal',
    production: 'Production',
  },
  segments: {
    warehouse: '{value} min per order',
    complaints: 'complaints: {value}',
    disposal: 'disposal: {value}',
    production: 'difference: {value}',
    productionMissing: 'no data for the period',
    ai: 'AI insights: {count}',
  },
  ai: {
    notesTitle: 'AI insights for this part',
  },
  kpi: {
    complaints: 'Complaints',
    complaintsPctOfOrders: 'Complaints / orders',
    complaintsPerDeliveryDay: 'Complaints per delivery day',
    complaintsValue: 'Complaint value',
    disposalValue: 'Disposal',
    disposalNote: '{pct}% of sales',
    disposalPctOfSales: 'Disposal / sales',
    pickingMinutesPerOrder: 'Picking — min per order',
    orders: 'Orders',
    ordersPerDeliveryDay: 'Orders per delivery day',
    pickingHours: 'Picking hours',
    pickingHoursPerDeliveryDay: 'Picking hours per delivery day',
    orderLines: 'Order lines',
    pickingMinutesPerLine: 'Picking — min per line',
    linesPerOrder: 'Lines per order',
    inventoryBalance: 'Stocktaking — balance',
    productionSales: 'Production — finished goods sales',
    productionCost: 'Production — finished goods cost',
  },
  checks: {
    COMPLAINT_CATEGORIES_SUM:
      'Sum of the 8 complaint categories ({actual}) differs from the total ({expected}).',
    PRODUCTION_MISSING_NOT_ZERO: 'Production: no data or only zeros — the report shows "no data", not 0.',
    WAREHOUSE_ORDERS_VS_ERP:
      'Orders in the warehouse form ({actual}) differ from ERP ({expected}) by {diffPct}%.',
  },
  categories: {
    QUALITY: 'Quality',
    MISSING_IN_DELIVERY: 'Missing in delivery',
    MISSING_ON_DOCUMENT: 'Missing on document',
    WEIGHT_QUANTITY: 'Weight / quantity',
    WRONG_PRODUCT: 'Wrong product',
    EXPIRY_DATE: 'Expiry date',
    PRICE: 'Price',
    OTHER: 'Other',
  },
  statuses: {
    OPEN: 'Open',
    IN_PROGRESS: 'In progress',
    ACCEPTED: 'Accepted',
    REJECTED: 'Rejected',
    CLOSED: 'Closed',
  },
  dimensions: {
    category: 'Category',
    product: 'Product',
    route: 'Route',
    driver: 'Driver / vehicle',
    customer: 'Customer',
  },
  sources: {
    ERP: 'from ERP',
    WAREHOUSE: 'from the warehouse form',
    REGISTER: 'from the register',
    IMPORT: 'from the import',
    MISSING: 'no data',
  },
  common: {
    unassigned: 'unassigned',
    scopedNote:
      'Orders and complaints of your team. Indicators mixing company-wide data with your team are hidden.',
    all: 'All',
    share: 'Share',
    count: 'Count',
    value: 'Value',
    total: 'Total',
    noData: 'no data',
    scopedChip: 'Your team',
  },
  warehouse: {
    title: 'Picking and orders',
    sources: 'Orders {orders}, lines {lines}',
    inventoryTitle: 'Stocktaking',
    inventoryPlus: 'Surplus (+)',
    inventoryMinus: 'Shortage (−)',
    inventoryBalance: 'Balance',
    inventoryHint: 'Surplus minus shortage in the current period',
    inventorySplit: 'Share of surplus and shortage',
    trendTitle: 'Orders and picking minutes — 12 months',
  },
  complaints: {
    categoriesTitle: 'Complaints in 8 categories',
    trendTitle: '12-month trend by category',
    breakdownTitle: 'Where complaints come from (current period)',
    statusesTitle: 'Statuses in the period',
    openRegister: 'Complaint register',
    sourceNote: 'Counts {source}',
    current: 'Current period',
    base: 'Base period',
    flagged: 'Flagged by AI',
    breakdownHint: 'The darker the cell, the more complaints in that category.',
    heatLegend: 'Fewer',
    heatMore: 'more complaints',
    noRegister: 'The matrix is built from the complaint register — this period counts come from the import.',
  },
  disposal: {
    topTitle: 'Most disposed products',
    entries: 'Entries',
    openRegister: 'Disposal register',
    trendTitle: 'Disposal — 12 months',
    trendHint: 'Value of goods disposed of in the month',
    trendMonth: 'Report month',
    trendOther: 'Previous months',
    monthTotal: 'Month total',
  },
  production: {
    title: 'Production — finished goods',
    sales: 'Finished goods sales',
    cost: 'Finished goods cost',
    diff: 'Difference',
    diffPct: 'Difference / sales',
    missingNote: 'No production data for this period — an empty field means "no data", not 0.',
    missingTitle: 'No production data for {period}',
    missingHint:
      'The report shows this field as "no data", not 0. Finished goods sales and cost are entered in the "Production" form of the month close.',
    fillForm: 'Fill in the Production form',
    diffHint: 'Sales minus cost of finished goods',
    diffPctHint: 'Difference as % of finished goods sales',
    trendTitle: 'Finished goods sales and cost — 12 months',
  },
  register: {
    complaintsTitle: 'Complaint register',
    complaintsDescription: 'Every complaint with category, customer, route, driver, product and document.',
    disposalsTitle: 'Disposal register',
    disposalsDescription: 'Disposal entries (RW) with product, value and reason.',
    back: 'Back to the section',
    add: 'Add',
    edit: 'Edit',
    remove: 'Delete',
    removeConfirm: 'Delete this entry? This cannot be undone.',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'Saved',
    removed: 'Deleted',
    search: 'Search (description, document, product, customer)',
    empty: 'No entries for the selected filters.',
    readOnly: 'Month closed — read only',
    showing: '{from}–{to} of {total}',
    prev: 'Previous',
    next: 'Next',
    totalValue: 'Total value: {value}',
    summary: 'Entries: {count} · total {value}',
    analysisTitle: 'Analysis: {dimension} × category',
    analysisEmpty: 'No complaints to analyse.',
    errors: {
      MBR_MONTH_CLOSED: 'The month is closed — reopen it to change the data.',
      MBR_COMPLAINT_SCOPE: 'You can register complaints only for customers within your scope.',
      MBR_COMPLAINT_DUPLICATE_REF: 'This protocol number is already in the register.',
      MBR_DISPOSAL_DUPLICATE_REF: 'This RW document number is already in the register.',
      MBR_REFERENCE_INVALID: 'The selected record does not exist — pick one from the suggestions.',
      generic: 'Could not save.',
    },
  },
  fields: {
    date: 'Date',
    category: 'Category',
    status: 'Status',
    customer: 'Customer',
    route: 'Route',
    driver: 'Driver',
    vehicle: 'Vehicle',
    product: 'Product',
    productName: 'Product (outside the catalogue)',
    quantity: 'Quantity',
    value: 'Value, PLN',
    description: 'Description',
    externalRef: 'Protocol / document no.',
    reason: 'Reason',
    documentRef: 'RW document no.',
    lookupPlaceholder: 'Start typing to search…',
    clear: 'Clear',
  },
};
